import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { MessageSquare, Search, Circle, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/layout/Navbar';
import RealtimeChat from '../components/chat/RealtimeChat';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { de, enUS } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatPage = () => {
  const { t, language } = useLanguage();
  const { token, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);

  // Get recipient from URL params
  const recipientIdFromUrl = searchParams.get('with');
  const weddingIdFromUrl = searchParams.get('wedding');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchConversations();
  }, [isAuthenticated, token, fetchConversations, navigate]);

  useEffect(() => {
    // If recipient is provided in URL, select that conversation
    if (recipientIdFromUrl && conversations.length > 0) {
      const conv = conversations.find(c => c.partner.id === recipientIdFromUrl);
      if (conv) {
        setSelectedConversation({
          recipientId: conv.partner.id,
          recipientName: conv.partner.name,
          weddingId: weddingIdFromUrl || ''
        });
      } else {
        // Create new conversation placeholder
        fetchUserInfo(recipientIdFromUrl);
      }
    }
  }, [recipientIdFromUrl, conversations, fetchUserInfo, weddingIdFromUrl]);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchUserInfo = useCallback(async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedConversation({
        recipientId: response.data.id,
        recipientName: response.data.name,
        weddingId: weddingIdFromUrl || ''
      });
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    }
  }, [token, weddingIdFromUrl]);

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: language === 'de' ? de : enUS
      });
    } catch {
      return '';
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const filteredConversations = conversations.filter(conv =>
    conv.partner.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleNewMessage = () => {
    // Refresh conversations list when a new message is received
    fetchConversations();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6 h-[calc(100vh-140px)]">
          {/* Conversations List */}
          <div className="w-80 flex-shrink-0 bg-white rounded-xl border border-border overflow-hidden flex flex-col">
            <div className="p-4 border-b border-border">
              <h2 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                {t('chat.conversations') || 'Conversations'}
              </h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('chat.search') || 'Search conversations...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {filteredConversations.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>{t('chat.noConversations') || 'No conversations yet'}</p>
                </div>
              ) : (
                filteredConversations.map((conv) => (
                  <button
                    key={conv.partner.id}
                    onClick={() => setSelectedConversation({
                      recipientId: conv.partner.id,
                      recipientName: conv.partner.name,
                      weddingId: ''
                    })}
                    className={`w-full p-4 flex items-start gap-3 hover:bg-secondary/50 transition-colors border-b border-border last:border-0 text-left ${
                      selectedConversation?.recipientId === conv.partner.id ? 'bg-secondary' : ''
                    }`}
                  >
                    <div className="relative">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {getInitials(conv.partner.name)}
                        </AvatarFallback>
                      </Avatar>
                      {conv.is_online && (
                        <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium truncate">{conv.partner.name}</span>
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                          {formatTime(conv.last_message.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground truncate">
                          {conv.last_message.sender_id === user?.id ? (
                            <span className="text-muted-foreground/70">{t('chat.you') || 'You'}: </span>
                          ) : null}
                          {conv.last_message.content}
                        </p>
                        {conv.unread_count > 0 && (
                          <Badge className="bg-primary text-white h-5 min-w-[20px] flex items-center justify-center rounded-full text-xs">
                            {conv.unread_count}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 bg-white rounded-xl border border-border overflow-hidden">
            {selectedConversation ? (
              <RealtimeChat
                recipientId={selectedConversation.recipientId}
                recipientName={selectedConversation.recipientName}
                weddingId={selectedConversation.weddingId}
                onNewMessage={handleNewMessage}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">{t('chat.selectConversation') || 'Select a conversation'}</p>
                  <p className="text-sm">{t('chat.selectConversationDesc') || 'Choose a conversation from the list to start chatting'}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
